import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const PrivacySection = () => {
  const [privacySettings, setPrivacySettings] = useState({
    dataCollection: true,
    analyticsTracking: false,
    marketingEmails: false,
    thirdPartySharing: false,
    profileVisibility: 'private',
    activityTracking: true
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleToggleSetting = (key) => {
    setPrivacySettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSaveSettings = async () => {
    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsLoading(false);
    console.log('Privacy settings saved');
  };

  const handleExportData = async () => {
    setIsLoading(true);
    
    // Simulate data export
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsLoading(false);
    console.log('Data export initiated');
  };

  const handleDeleteAccount = async () => {
    setIsLoading(true);
    
    // Simulate account deletion
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsLoading(false);
    setShowDeleteConfirm(false);
    console.log('Account deletion initiated');
  };

  const privacyOptions = [
    {
      key: 'dataCollection',
      title: 'Data Collection',
      description: 'Allow collection of usage data to improve the application',
      icon: 'Database',
      color: 'text-primary'
    },
    {
      key: 'analyticsTracking',
      title: 'Analytics Tracking',
      description: 'Enable anonymous analytics to help us understand user behavior',
      icon: 'BarChart3',
      color: 'text-secondary'
    },
    {
      key: 'marketingEmails',
      title: 'Marketing Communications',
      description: 'Receive promotional emails about new features and offers',
      icon: 'Mail',
      color: 'text-accent'
    },
    {
      key: 'thirdPartySharing',
      title: 'Third-Party Data Sharing',
      description: 'Allow sharing of anonymized data with trusted partners',
      icon: 'Share2',
      color: 'text-warning'
    },
    {
      key: 'activityTracking',
      title: 'Activity Tracking',
      description: 'Track your activity to provide personalized recommendations',
      icon: 'Activity',
      color: 'text-success'
    }
  ];

  const dataCategories = [
    {
      category: 'Personal Information',
      items: ['Name', 'Email', 'Phone Number', 'Profile Picture'],
      size: '2.3 MB'
    },
    {
      category: 'Portfolio Data',
      items: ['Stock Holdings', 'Transaction History', 'Performance Metrics'],
      size: '15.7 MB'
    },
    {
      category: 'Preferences',
      items: ['App Settings', 'Notification Preferences', 'Theme Settings'],
      size: '0.8 MB'
    },
    {
      category: 'Activity Logs',
      items: ['Login History', 'Search History', 'Page Views'],
      size: '4.2 MB'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Privacy Controls */}
      <div className="card-elevated p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Shield" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Privacy Controls</h2>
            <p className="text-sm text-muted-foreground">Manage how your data is collected and used</p>
          </div>
        </div>

        <div className="space-y-4">
          {privacyOptions.map((option) => (
            <div key={option.key} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-background rounded-lg flex items-center justify-center">
                  <Icon name={option.icon} size={20} className={option.color} />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-foreground">{option.title}</h4>
                  <p className="text-xs text-muted-foreground">{option.description}</p>
                </div>
              </div>
              <Checkbox
                checked={privacySettings[option.key]}
                onChange={() => handleToggleSetting(option.key)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Data Overview */}
      <div className="card-elevated p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
            <Icon name="HardDrive" size={20} className="text-secondary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Your Data</h2>
            <p className="text-sm text-muted-foreground">Overview of data we store about you</p>
          </div>
        </div>

        <div className="space-y-4">
          {dataCategories.map((category, index) => (
            <div key={index} className="p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-foreground">{category.category}</h4>
                <span className="text-xs text-muted-foreground font-mono">{category.size}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {category.items.map((item, itemIndex) => (
                  <span
                    key={itemIndex}
                    className="text-xs bg-background px-2 py-1 rounded-full text-muted-foreground"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 bg-accent/10 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Info" size={16} className="text-accent" />
            <h4 className="text-sm font-medium text-foreground">Total Data Size</h4>
          </div>
          <p className="text-sm text-muted-foreground">
            We store approximately <span className="font-mono font-semibold">23.0 MB</span> of data associated with your account.
          </p>
        </div>
      </div>

      {/* Data Rights */}
      <div className="card-elevated p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="FileText" size={20} className="text-accent" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Your Data Rights</h2>
            <p className="text-sm text-muted-foreground">Exercise your rights regarding your personal data</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-3 mb-3">
              <Icon name="Download" size={20} className="text-success" />
              <h4 className="text-sm font-medium text-foreground">Export Your Data</h4>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Download a copy of all your data in a portable format
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportData}
              loading={isLoading}
              iconName="Download"
              iconPosition="left"
              fullWidth
            >
              Request Data Export
            </Button>
          </div>

          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-3 mb-3">
              <Icon name="Edit" size={20} className="text-primary" />
              <h4 className="text-sm font-medium text-foreground">Correct Your Data</h4>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Update or correct any inaccurate personal information
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => console.log('Navigate to profile edit')}
              iconName="Edit"
              iconPosition="left"
              fullWidth
            >
              Edit Profile
            </Button>
          </div>

          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-3 mb-3">
              <Icon name="Pause" size={20} className="text-warning" />
              <h4 className="text-sm font-medium text-foreground">Restrict Processing</h4>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Limit how we process your personal data
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => console.log('Restrict processing')}
              iconName="Pause"
              iconPosition="left"
              fullWidth
            >
              Restrict Processing
            </Button>
          </div>

          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-3 mb-3">
              <Icon name="Trash2" size={20} className="text-destructive" />
              <h4 className="text-sm font-medium text-foreground">Delete Your Data</h4>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Permanently delete your account and all associated data
            </p>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
              iconName="Trash2"
              iconPosition="left"
              fullWidth
            >
              Delete Account
            </Button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-modal flex items-center justify-center p-4">
          <div className="bg-card rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-destructive/10 rounded-lg flex items-center justify-center">
                <Icon name="AlertTriangle" size={20} className="text-destructive" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Delete Account</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-foreground mb-4">
                Are you sure you want to delete your account? This will permanently remove:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                <li>• All your portfolio data and transaction history</li>
                <li>• Your personal information and preferences</li>
                <li>• All saved watchlists and alerts</li>
                <li>• Your account access and login credentials</li>
              </ul>
            </div>
            
            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteAccount}
                loading={isLoading}
                iconName="Trash2"
                iconPosition="left"
              >
                Delete Account
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          variant="default"
          onClick={handleSaveSettings}
          loading={isLoading}
          iconName="Save"
          iconPosition="left"
        >
          Save Privacy Settings
        </Button>
      </div>
    </div>
  );
};

export default PrivacySection;